// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Core__Option from "@rescript/core/src/Core__Option.bs.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";
import * as Validation$OrcaPaymentPage from "../validation/Validation.bs.js";
import * as PaymentEventTypes$OrcaPaymentPage from "./PaymentEventTypes.bs.js";

function shouldEmitEvent(eventType, subscribedEvents) {
  if (subscribedEvents.length === 0) {
    return false;
  } else {
    return subscribedEvents.some(subscribed => subscribed === eventType);
  }
}

function buildCardInfo(cardNumber, expiry, cvc, brand) {
  let cleanNumber = Validation$OrcaPaymentPage.clearSpaces(cardNumber);
  let len = cleanNumber.length;
  let bin = len >= 6 ? cleanNumber.substring(0, 6) : undefined;
  let isCardNumberComplete = Validation$OrcaPaymentPage.isCardNumberEqualsMax(cleanNumber, brand);
  let last4 = isCardNumberComplete ? cleanNumber.substring(len - 4 | 0, len) : undefined;
  let match = Validation$OrcaPaymentPage.getExpiryDates(expiry);
  let year = match[1];
  let month = match[0];
  let expiryMonth = month !== "" ? month : undefined;
  let expiryYear = year.length === 4 ? year : undefined;
  let formattedExpiry = month !== "" && year.length === 4 ? expiry : undefined;
  let isCardNumberValid = Validation$OrcaPaymentPage.cardValid(cardNumber, brand);
  let isExpiryValid = Validation$OrcaPaymentPage.checkCardExpiry(expiry);
  let isCardNumberComplete$1 = isCardNumberComplete && isCardNumberValid;
  let isCvcComplete = Validation$OrcaPaymentPage.checkCardCVC(cvc, brand);
  return {
    bin: bin,
    last4: last4,
    brand: brand === "" ? undefined : brand,
    expiryMonth: expiryMonth,
    expiryYear: expiryYear,
    formattedExpiry: formattedExpiry,
    isCardNumberComplete: isCardNumberComplete$1,
    isCvcComplete: isCvcComplete,
    isExpiryComplete: isExpiryValid,
    isCardNumberValid: isCardNumberValid,
    isExpiryValid: isExpiryValid
  };
}

function buildCardInfoFromSavedCard(bin, last4, brand, expiryMonth, expiryYear, isCvcComplete) {
  let shortYear = expiryYear.length >= 4 ? expiryYear.substring(2, 4) : expiryYear;
  let formattedExpiry = expiryMonth + `/` + shortYear;
  return {
    bin: bin,
    last4: last4,
    brand: brand,
    expiryMonth: expiryMonth,
    expiryYear: expiryYear,
    formattedExpiry: formattedExpiry,
    isCardNumberComplete: true,
    isCvcComplete: isCvcComplete,
    isExpiryComplete: true,
    isCardNumberValid: true,
    isExpiryValid: true
  };
}

function cardInfoToJson(info) {
  return Object.fromEntries([
    [
      "bin",
      Core__Option.getOr(Core__Option.map(info.bin, prim => prim), null)
    ],
    [
      "last4",
      Core__Option.getOr(Core__Option.map(info.last4, prim => prim), null)
    ],
    [
      "brand",
      Core__Option.getOr(Core__Option.map(info.brand, prim => prim), null)
    ],
    [
      "expiryMonth",
      Core__Option.getOr(Core__Option.map(info.expiryMonth, prim => prim), null)
    ],
    [
      "expiryYear",
      Core__Option.getOr(Core__Option.map(info.expiryYear, prim => prim), null)
    ],
    [
      "formattedExpiry",
      Core__Option.getOr(Core__Option.map(info.formattedExpiry, prim => prim), null)
    ],
    [
      "isCardNumberComplete",
      info.isCardNumberComplete
    ],
    [
      "isCvcComplete",
      info.isCvcComplete
    ],
    [
      "isExpiryComplete",
      info.isExpiryComplete
    ],
    [
      "isCardNumberValid",
      info.isCardNumberValid
    ],
    [
      "isExpiryValid",
      info.isExpiryValid
    ]
  ]);
}

function buildPaymentMethodStatusEvent(paymentMethod, paymentMethodType, isSavedPaymentMethodOpt, isOneClickWalletOpt) {
  let isSavedPaymentMethod = isSavedPaymentMethodOpt !== undefined ? isSavedPaymentMethodOpt : false;
  let isOneClickWallet = isOneClickWalletOpt !== undefined ? isOneClickWalletOpt : false;
  return {
    paymentMethod: paymentMethod,
    paymentMethodType: paymentMethodType,
    isSavedPaymentMethod: isSavedPaymentMethod,
    isOneClickWallet: isOneClickWallet
  };
}

function paymentMethodStatusEventToJson(paymentMethod, paymentMethodType, isSavedPaymentMethodOpt, isOneClickWalletOpt) {
  let isSavedPaymentMethod = isSavedPaymentMethodOpt !== undefined ? isSavedPaymentMethodOpt : false;
  let isOneClickWallet = isOneClickWalletOpt !== undefined ? isOneClickWalletOpt : false;
  return Object.fromEntries([
    [
      "paymentMethod",
      paymentMethod
    ],
    [
      "isSavedPaymentMethod",
      isSavedPaymentMethod
    ],
    [
      "paymentMethodType",
      paymentMethodType
    ],
    [
      "isOneClickWallet",
      isOneClickWallet
    ]
  ]);
}

function buildFormStatusEvent(status) {
  return {
    status: PaymentEventTypes$OrcaPaymentPage.formStatusValueToString(status)
  };
}

function formStatusEventToJson(status) {
  return Object.fromEntries([[
      "status",
      PaymentEventTypes$OrcaPaymentPage.formStatusValueToString(status)
    ]]);
}

function buildPaymentMethodInfoAddress(country, state, postalCode) {
  return {
    country: country,
    state: state,
    postalCode: postalCode
  };
}

function paymentMethodInfoAddressToJson(country, state, postalCode) {
  return Object.fromEntries([
    [
      "country",
      country
    ],
    [
      "state",
      state
    ],
    [
      "postalCode",
      postalCode
    ]
  ]);
}

function computeFormStatus(isComplete, isEmpty) {
  if (isComplete) {
    return "Complete";
  } else if (isEmpty) {
    return "Empty";
  } else {
    return "Filling";
  }
}

function buildCvcStatusEvent(isCvcFocused, isCvcBlur, isCvcEmptyOpt, isCvcCompleteOpt) {
  let isCvcEmpty = isCvcEmptyOpt !== undefined ? isCvcEmptyOpt : true;
  let isCvcComplete = isCvcCompleteOpt !== undefined ? isCvcCompleteOpt : false;
  return {
    isCvcFocused: isCvcFocused,
    isCvcBlur: isCvcBlur,
    isCvcEmpty: isCvcEmpty,
    isCvcComplete: isCvcComplete
  };
}

function cvcStatusEventToJson(event) {
  let baseFields = [
    [
      "isCvcEmpty",
      event.isCvcEmpty
    ],
    [
      "isCvcComplete",
      event.isCvcComplete
    ]
  ];
  let v = event.isCvcFocused;
  let withFocused = v !== undefined ? baseFields.concat([[
        "isCvcFocused",
        v
      ]]) : baseFields;
  let v$1 = event.isCvcBlur;
  let withBlur = v$1 !== undefined ? withFocused.concat([[
        "isCvcBlur",
        v$1
      ]]) : withFocused;
  return Object.fromEntries([[
      "cvcStatus",
      Object.fromEntries(withBlur)
    ]]);
}

function buildSurchargeEvent(surcharge, taxOnSurchargeOpt, displaySurchargeAmountOpt, displayTaxOnSurchargeAmountOpt, displayTotalSurchargeAmountOpt) {
  let taxOnSurcharge = taxOnSurchargeOpt !== undefined ? Primitive_option.valFromOption(taxOnSurchargeOpt) : undefined;
  let displaySurchargeAmount = displaySurchargeAmountOpt !== undefined ? displaySurchargeAmountOpt : 0.0;
  let displayTaxOnSurchargeAmount = displayTaxOnSurchargeAmountOpt !== undefined ? displayTaxOnSurchargeAmountOpt : 0.0;
  let displayTotalSurchargeAmount = displayTotalSurchargeAmountOpt !== undefined ? displayTotalSurchargeAmountOpt : 0.0;
  return {
    surcharge: surcharge,
    taxOnSurcharge: taxOnSurcharge,
    displaySurchargeAmount: displaySurchargeAmount,
    displayTaxOnSurchargeAmount: displayTaxOnSurchargeAmount,
    displayTotalSurchargeAmount: displayTotalSurchargeAmount
  };
}

function surchargeEventToJson(event) {
  let surchargeJson = Object.fromEntries([
    [
      "type",
      event.surcharge.type
    ],
    [
      "value",
      event.surcharge.value
    ]
  ]);
  return Object.fromEntries([
    [
      "surcharge",
      surchargeJson
    ],
    [
      "taxOnSurcharge",
      Core__Option.getOr(Core__Option.map(event.taxOnSurcharge, prim => prim), null)
    ],
    [
      "displaySurchargeAmount",
      event.displaySurchargeAmount
    ],
    [
      "displayTaxOnSurchargeAmount",
      event.displayTaxOnSurchargeAmount
    ],
    [
      "displayTotalSurchargeAmount",
      event.displayTotalSurchargeAmount
    ]
  ]);
}

export {
  shouldEmitEvent,
  buildCardInfo,
  buildCardInfoFromSavedCard,
  cardInfoToJson,
  buildPaymentMethodStatusEvent,
  paymentMethodStatusEventToJson,
  buildFormStatusEvent,
  formStatusEventToJson,
  buildPaymentMethodInfoAddress,
  paymentMethodInfoAddressToJson,
  computeFormStatus,
  buildCvcStatusEvent,
  cvcStatusEventToJson,
  buildSurchargeEvent,
  surchargeEventToJson,
}
/* No side effect */
